/* eslint-disable react-hooks/exhaustive-deps */
import './app.scss'
import React, { useEffect, useState, useMemo } from 'react'
import { Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { RouteAuthorizer } from './routes/Authorizer'
import { connect, useSelector } from 'react-redux'
import { bindActionCreators } from 'redux'
import { ToastContainer } from 'react-toastify'
import PaymentExpiredNotification from './components/PaymentExpiredNotification'
import vanillaOidc from '@axa-fr/vanilla-oidc'
import dkvConfiguration from 'constants/dkv.constants'

import { nxAuthTokenAct } from 'redux_new/actions'
import operatorActions from './redux/actions/user'

import Dashboard from './views/Dashboard'
import LandingRoutes from './routes/Landing'
import OutboundModal from './components/OutboundModal'
import SyncErrorModal from 'components/ChannelView/Channels/ChatMail/AddAccountModals/SyncErrorModal'
import channels from './redux/channels/actions'
import Nixi1Spinner from 'components/Nixi1Spinner'
import nixi1Theme from './theme'

const PREFERS_DARK_MODE = false

const PrivateRoute = ({ component: Component, auth, dispatch, ...rest }) => {
  if (!auth) {
    return <Redirect to='/' />
  }
  return <Route path={rest.path} render={(props) => <Component {...props} />} />
}

const AuthorizedDashboard = RouteAuthorizer(['user'])(Dashboard)

const App = ({ operator: { role, authToken }, nxAuthToken, dispatch, outbound }) => {
  const [loading, setLoading] = useState(true)

  const operatorActs = bindActionCreators(operatorActions, dispatch)
  const onNxAuthToken = bindActionCreators(nxAuthTokenAct, dispatch)

  const location = useLocation()

  const history = useHistory()
  const chatmailSync = useSelector(state => state?.channels?.chatmailSync)
  const [isOpenSyncErrorModal, setIsOpenSyncErrorModal] = useState(chatmailSync.map(it => ({ ...it, openModal: null })))

  useEffect(() => {
    if (chatmailSync?.length > 0) {
      let _chatmailSync = []
      chatmailSync.forEach(it => {
        if (it?.type === 'syncError') {
          const _openModal = isOpenSyncErrorModal.find(it2 => it2.channelId === it.channelId)?.openModal || true
          _chatmailSync.push({ ...it, openModal: _openModal })
        }
      })
      if (_chatmailSync.length > 0) setIsOpenSyncErrorModal(_chatmailSync)
    }
  }, [chatmailSync])

  useEffect(() => {
    if (dkvConfiguration.loginWithOpenId) {
      if (nxAuthToken) {
        operatorActs.getOperator(nxAuthToken).finally(() => setLoading(false))
      } else {
        const vanillaOidcPromise = vanillaOidc.init(dkvConfiguration.configuration)

        vanillaOidcPromise
          .then((status) => {
            if (status.type !== 'callback') {
              if (!status.user) {
                vanillaOidc.signinRedirect()
              }
            } else {
              // Here is where I get the callback from oidc
              const oidcData = JSON.parse(sessionStorage.getItem(dkvConfiguration.sessionStorage))

              onNxAuthToken.add(oidcData.access_token, oidcData.refresh_token)
              operatorActs.getOperator(oidcData.access_token).finally(() => setLoading(false))
            }
          })
          .catch((error) => {
            if (error.message.includes('login_required') && !nxAuthToken) {
              sessionStorage.removeItem(dkvConfiguration.sessionStorage)
              window.location.reload()
            }
          })
      }
    } else {
      operatorActs.getOperator(nxAuthToken, false, location.search).finally(() => setLoading(false))
    }
  }, [])

  const theme = useMemo(() => createTheme(nixi1Theme(PREFERS_DARK_MODE)), [PREFERS_DARK_MODE])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {
        loading
          ? (
            <div className='app_loading-root'>
              <Nixi1Spinner width='90px' height='90px' />
            </div>
          ) : (
            <>
              <Switch>
                <PrivateRoute path='/dashboard' auth={authToken} component={AuthorizedDashboard} dispatch={dispatch} />

                <Route
                  path='/'
                  name='Landing'
                  render={(routeProps) =>
                    authToken ? <Redirect to='/dashboard/overview' /> : <LandingRoutes {...routeProps} />
                  }
                />
              </Switch>

              <ToastContainer
                className='app_reacttoastify_toaster'
                progressClassName='app_toaster-progress-bar'
                bodyClassName='app_toaster-body'
                autoClose={3000}
              />

              <PaymentExpiredNotification />

              {
                outbound.modals.map((outbound, index) => outbound.isOpen && (
                  <OutboundModal
                    key={index}
                    outboundIndex={index}
                    outbound={outbound}
                  />
                ))
              }

              {
                isOpenSyncErrorModal?.length > 0
                  ? isOpenSyncErrorModal.map((it, idx) => (
                    <SyncErrorModal
                      key={idx}
                      isOpen={it.openModal}
                      toggle={() => {
                        setIsOpenSyncErrorModal(isOpenSyncErrorModal.map(it2 => it2.channelId === it.channelId ? { ...it2, openModal: false } : it2))
                        dispatch(channels.updateChatmailSync(chatmailSync.filter(it2 => it2.channelId !== it.channelId), true))
                      }}
                      data={it}
                      onClick={() => {
                        setIsOpenSyncErrorModal(isOpenSyncErrorModal.map(it2 => it2.channelId === it.channelId ? { ...it2, openModal: false } : it2))
                        dispatch(channels.updateChatmailSync(chatmailSync.filter(it2 => it2.channelId !== it.channelId), true))
                        history.push(`/dashboard/configurations/channels/${it.channelId}`)
                      }}
                    />
                  ))
                  : null
              }
            </>
          )
      }
    </ThemeProvider>
  )
}

export default connect((state) => ({
  nxAuthToken: state.nxAuthToken.token,
  role: state.user.role,
  company: state.companies,
  operator: state.user,
  outbound: state.outbound,
  locale: state.i18n?.locale
}))(App)
